import { Helmet } from 'react-helmet';
import { Box, Heading, Text, SimpleGrid, Image } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import nextArrow from '../assets/nextArrow.png';
import { extractImageFromContent, getBlogs, stripHtml } from '../utilities/blogUtils';

function AllNews() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBlogs(setBlogs, setLoading);
  }, []);


  return (
    <>
      <Helmet>
        <title>News and Updates | Leaderbored</title>
      </Helmet>
      <Box mx='auto' maxW='1200px' color='white' px={{ base: 4, md: 8 }} py={8}>

        {/* Main Header */}
        <Box maxW='640px' mx='auto' textAlign='center'>
          <Heading mb='24px' textAlign='center'>
            All The Latest News!
          </Heading>
          <Text>
            Stay updated with the latest news and updates from Leaderbored! Here you'll find all the recent announcements, game releases, events, and more.
          </Text>
        </Box>

        {loading ? (
          <Box textAlign="center" py={16}>
            <Text>Loading news...</Text>
          </Box>
        ) : blogs.length === 0 ? (
          <Box textAlign="center" py={16}>
            <Heading fontSize="2xl" mb={4}>No News Available</Heading>
            <Text color="whiteAlpha.700">
              Check back soon for updates and announcements!
            </Text>
          </Box>
        ) : (
          <>
            <Box my={16}>
              <NewsBig
                title={blogs[0].title}
                description={stripHtml(blogs[0].content)}
                imageUrl={extractImageFromContent(blogs[0].content)}
                gameName={blogs[0].author.displayName}
                date={new Date(blogs[0].published).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                postId={blogs[0].id}
                url={blogs[0].url}
                labels={blogs[0].labels}
                author={blogs[0].author}
                published={blogs[0].published}
                content={blogs[0].content}
              />
            </Box>

            {blogs.length > 1 && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={12} spacingY={16} my={16}>
                {blogs.slice(1).map((blog) => (
                  <NewsSmall
                    key={blog.id}
                    title={blog.title}
                    description={stripHtml(blog.content)}
                    gameName={blog.author.displayName}
                    date={new Date(blog.published).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                    imageUrl={extractImageFromContent(blog.content) || 'https://placehold.co/400x250?text=News'}
                    postId={blog.id}
                    url={blog.url}
                    labels={blog.labels}
                    author={blog.author}
                    published={blog.published}
                    content={blog.content}
                  />
                ))}
              </SimpleGrid>
            )}
          </>
        )}
      </Box>
    </>
  )
}

export default AllNews;


function NewsBig({ title, description, gameName, date, imageUrl, postId, url, labels, author, published, content }) {
  return (
    <Box
      as={Link}
      to={`/news/${postId}`}
      state={{ title, description, gameName, date, imageUrl, url, labels, author, published, content }}
      bgColor='secondary'
      boxShadow='8px 8px 0px var(--chakra-colors-primaryDark)'
      display='block'
      _hover={{ transform: 'translateY(-4px)', transition: 'transform 0.2s' }}
      cursor='pointer'
      textDecoration='none'
    >
      <Box
        position='relative'
      >

        {/* Top Title */}
        <Box
          position='absolute'
          display='inline-block'
          paddingLeft={{ base: 16, md: 20 }}
          paddingY={2}
          bgColor='secondary'
          paddingRight='125px'
          clipPath='polygon(0 0, 100% 0, calc(100% - 50px) 100%, 0 100%)'
          boxShadow='8px 8px 0px var(--chakra-colors-primaryDark)'
        >
          <Heading fontSize='2xl' mb={0} fontStyle={'italic'}>Featured!</Heading>
        </Box>

        {/* Image Container */}
        <Box>
          <Image
            width='100%'
            maxH='500px'
            src={imageUrl || 'https://placehold.co/100x50?text=Hello+World'}
            alt={title}
            objectFit='cover'
            objectPosition='center'
          />
        </Box>
      </Box>

      {/* Text Content */}
      <Box
        paddingX={{ base: 16, md: 20 }}
        paddingY={8}
      >
        <Heading
          fontSize='4xl'
          mb={4}
          textTransform='uppercase'
          textAlign='center'
        >
          {title}
        </Heading>
        <Text fontSize='lg' mb={4} noOfLines={3}>
          {description}
        </Text>
      </Box>

      {/* Lower Elements */}
      <Box
        padding={{ base: 2, md: 4 }}
        position='relative'
      >
        <GameInfoBadges gameName={gameName} date={date} />
        <Box
          position='absolute'
          right={{ base: 2, md: 4 }}
          top='50%'
          transform='translateY(-50%)'
        >
          <Image
            src={nextArrow}
            height='3rem'
          />
        </Box>
      </Box>
    </Box>
  )
}

function NewsSmall({ title, description, gameName, date, imageUrl, postId, url, labels, author, published, content }) {
  return (
    <Box
      as={Link}
      to={`/news/${postId}`}
      state={{ title, description, gameName, date, imageUrl, url, labels, author, published, content }}
      bgColor='primary'
      borderRadius='16px'
      boxShadow='8px 8px 0px var(--chakra-colors-primaryDark)'
      overflow='hidden'
      padding='4'
      display='block'
      _hover={{ transform: 'translateY(-4px)', transition: 'transform 0.2s' }}
      cursor='pointer'
      textDecoration='none'
    >
      {/* Image */}
      <Box>
        <Image
          width='100%'
          height='250px'
          src={imageUrl || 'https://placehold.co/400x250?text=News'}
          alt={title}
          objectFit='cover'
          objectPosition='center'
        />
      </Box>

      {/* Content */}
      <Box paddingY={4}>
        {/* Title */}
        <Heading fontSize='2xl' mb='2'>
          {title}
        </Heading>

        {/* Description */}
        <Text mb={4} noOfLines={2}>
          {description}
        </Text>

        {/* Badges */}
        <GameInfoBadges gameName={gameName} date={date} truncate={true} showBackground={false} />
      </Box>
    </Box>
  );
}


function GameInfoBadges({ gameName, date, truncate = false, showBackground = true }) {
  return (
    <Box
      display='grid'
      gridTemplateColumns='1fr auto 1fr'
      alignItems='center'
      width='100%'
    >

      <Box justifySelf='end'>
        <Badge icon={true} text={gameName} truncate={truncate} showBackground={showBackground} />
      </Box>

      <Box
        width='0.3rem'
        height='2rem'
        backgroundColor='white'
        borderRadius='full'
        mx={4}
      />

      <Box justifySelf='start'>
        <Badge icon={true} text={date} truncate={truncate} showBackground={showBackground} />
      </Box>
    </Box>
  )
}

function Badge({
  icon,
  text,
  truncate = false,
  showBackground = true
}) {
  return (
    <Box
      bgColor={showBackground ? 'primary' : 'transparent'}
      width='fit-content'
      maxWidth={truncate ? '180px' : 'none'}
      paddingX={4}
      paddingY={2}
      display='flex'
      alignItems='center'
      gap={2}
    >
      {icon && <Box width='16px' height='16px' bgColor='blue.300' borderRadius='full' flexShrink={0} />}
      <Text fontWeight='semibold' noOfLines={truncate ? 1 : undefined}>
        {text}
      </Text>
    </Box>
  )
}